import {Segment, Tooltip} from 'platform/components';
import {HStack, Icon, Show, Space, Text} from 'platform/foundation';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import {Fragment, type ReactNode} from 'react';

import {not} from 'ramda';

import i18n from '@dms/i18n';

import type {LanguageContentStatus} from '../../utils/getLanguageContentStatus';

export function AdvertisementPreviewLanguageSwitcher(props: {
  activeLanguage: string;
  languageOptions: Array<{value: string; label: ReactNode}>;
  onLanguageChange: (language: string) => void;
  'data-testid': string;
}) {
  const languageDotsTooltipData: Array<{
    status: LanguageContentStatus;
    label: string;
    value: string;
  }> = [
    {
      status: 'complete',
      label: i18n.t('general.labels.color.green'),
      value: i18n.t('page.advertisement.preview.languageDotsTooltipComplete'),
    },
    {
      status: 'partial',
      label: i18n.t('general.labels.color.orange'),
      value: i18n.t('page.advertisement.preview.languageDotsTooltipPartial'),
    },
    {
      status: 'empty',
      label: i18n.t('general.labels.color.black.subtle'),
      value: i18n.t('page.advertisement.preview.languageDotsTooltipEmpty'),
    },
  ];
  const languageDotsTooltipContent: ReactNode = (
    <>
      {languageDotsTooltipData.map((tooltipItem, index) => {
        const isFirstItem = index === 0;

        return (
          <Fragment key={tooltipItem.status}>
            <Show when={not(isFirstItem)}>
              <Space vertical={1} />
            </Show>

            <HStack spacing={1} align="flex-start">
              <LanguageTooltipStatusDot $status={tooltipItem.status} />
              <Text size="xSmall" color="white">
                <strong>{tooltipItem.label}: </strong>
                {tooltipItem.value}
              </Text>
            </HStack>
          </Fragment>
        );
      })}
    </>
  );

  return (
    <HStack spacing={2} align="center" wrap>
      <HStack spacing={1} align="center">
        <Text size="xSmall" color="secondary">
          {i18n.t('page.advertisement.preview.language')}
        </Text>
        <Tooltip description={languageDotsTooltipContent} childrenWrapperDisplay="flex">
          <HelpTrigger
            type="button"
            aria-label={i18n.t('page.advertisement.preview.translationStatusHelp')}
          >
            <Icon value="action/help" color="text.secondary" size={4} />
          </HelpTrigger>
        </Tooltip>
      </HStack>
      <Segment
        value={props.activeLanguage}
        options={props.languageOptions}
        onChange={props.onLanguageChange}
        data-testid={`${props['data-testid']}-language-segment`}
      />
    </HStack>
  );
}
export const LanguageOptionLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({theme}) => theme.getSize(1)};
`;

export const LanguageStatusDot = styled.span<{$status: LanguageContentStatus}>`
  width: ${({theme}) => theme.getSize(2)};
  height: ${({theme}) => theme.getSize(2)};
  flex: 0 0 auto;
  border-radius: 50%;
  background: ${({theme, $status}) =>
    match($status)
      .with('complete', () => theme.colors.severity.success)
      .with('partial', () => theme.colors.severity.warning)
      .with('empty', () => theme.colors.palettes.neutral[50][100])
      .exhaustive()};
`;

const LanguageTooltipStatusDot = styled(LanguageStatusDot)`
  align-self: center;
`;

export const VisuallyHiddenLabel = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
`;

const HelpTrigger = styled.button`
  display: inline-flex;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: help;
  user-select: none;
  &:focus-visible {
    outline: ${({theme}) => `${theme.outline.default} ${theme.colors.general.accent}`};
    outline-offset: ${({theme}) => theme.outline.offset};
  }
`;
